import {
  ACCOUNT_V3_DECISION_STATUSES,
  AccountAccessMode,
  AccountV3DecisionStatus,
} from 'src/app/domain/governance/types/governance.types';

/**
 * Narrows an untrusted value (Firestore data, stale client state) to a canonical
 * account V3 decision status. Exact match only — no case folding or trimming.
 */
export function isAccountV3DecisionStatus(value: unknown): value is AccountV3DecisionStatus {
  return ACCOUNT_V3_DECISION_STATUSES.includes(value as AccountV3DecisionStatus);
}

/**
 * Resolves the access mode every user of an account inherits from the account's
 * V3 decision. Only an exact `'ACCEPTED'` resolves to `FULL_V3`; every other
 * value resolves to `V2_COMPATIBILITY_MODE`.
 *
 * **This function narrows a decision status that is already known to exist. It
 * does not decide what an untagged account gets.** An account with no
 * `governance/v3Decision` document is a deliberate exclusion — commercially
 * safe accounts are left untagged on purpose and receive full V3 — and that
 * case is resolved upstream by `GovernanceState.accessMode`, which returns
 * `null` (no enforcement) for loaded-but-absent and never calls this function.
 * The Firestore rules agree: `compatWritesBlocked()` guards on `exists()`, so
 * absence never blocks a write.
 *
 * **Do not "fix" the missing-value branch to return `FULL_V3`.** It stays
 * fail-closed on purpose: reaching it with `undefined`/`null` means a caller
 * could not distinguish *intentionally absent* from *failed to read*, and the
 * safe answer to that ambiguity is compatibility mode. Grant full access from a
 * caller that has positively established absence, not from here.
 */
export function resolveAccountAccessMode(decision: unknown): AccountAccessMode {
  return isAccountV3DecisionStatus(decision) && decision === 'ACCEPTED' ? 'FULL_V3' : 'V2_COMPATIBILITY_MODE';
}
